import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import {
  Heading,
  Input,
  Button,
  CollapsibleContentContainer,
  ThreeDotMenu,
  JSONEditor,
} from '@mm/components';
import { useViewActions } from '@mm/state/actions/viewActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import { isEqual } from 'lodash';
import { colors } from '@mm/utils/src/colors';

import { SlideoutMenu } from '../../components/SlideoutMenu';
import styles from './EditWebhookSuppressionListSlideoutEditor.module.css';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import { SuppressionList } from '@mm/state/model/SuppressionListModel';
import { ConnectionRequestType } from '@mm/state/model/ConnectionModel';
import { useSuppressionListsCRUDActions } from '@mm/state/actions/SuppressionListsCRUDActions';
import EditRequestTypeAndURL from '../../../../features/connections/panels/EditConnectionPanel/EditRequestTypeAndURL/EditRequestTypeAndURL';
import { StoreState } from '@mm/state/store-interfaces';

export interface EditWebhookSuppressionListSlideoutEditorProps {
  suppressionListId: number;
}

export const EditWebhookSuppressionListSlideoutEditor = ({
  suppressionListId,
}: EditWebhookSuppressionListSlideoutEditorProps) => {
  const viewActions = useViewActions();
  const modalActions = useModalActions();
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const suppressionLists = useSelector(
    (state: StoreState) => state.suppressionLists.entities,
  );

  const [suppressionList, setSuppressionList] = useState<
    SuppressionList | undefined
  >(
    suppressionLists?.find(suppression => suppression.id === suppressionListId),
  );
  const [nameEditMode, setNameEditMode] = useState(false);
  const { setRef } = useSlideoutOnClose();
  const [localName, setLocalName] = useState(suppressionList?.name || '');

  const { retrieveById, update, deleteList } = useSuppressionListsCRUDActions();

  useEffect(() => {
    const getConnection = async () => {
      if (activeFbPageId && suppressionListId && !suppressionList) {
        const newSuppressionList = await retrieveById(
          activeFbPageId,
          suppressionListId,
        );

        setSuppressionList(newSuppressionList);
        setLocalName(newSuppressionList?.name || '');
      }
    };
    getConnection();
  }, [activeFbPageId, suppressionListId, suppressionList]);

  const validateJSONPayload = useCallback(
    (data, listParams): Record<any, any> => {
      const k = Object.entries(data).find((k, v) => !v)?.[0] || '';

      if (data[k] instanceof Error) {
        return {};
      }

      if (isEqual(data[k], listParams[k])) {
        return {};
      }

      return data;
    },
    [suppressionList],
  );

  const handleJSONFieldsSave = async data => {
    if (!suppressionList?.suppressionWebhook) {
      return;
    }

    const jsonFields = validateJSONPayload(
      data,
      suppressionList?.suppressionWebhook,
    );

    if (!Object.values(jsonFields).length) {
      return;
    }

    const res = await update(activeFbPageId, {
      ...suppressionList,
      suppressionWebhook: {
        ...suppressionList.suppressionWebhook,
        ...jsonFields,
      },
    });

    setSuppressionList(res);
  };

  const updateSuppressionList = async (
    updatedFields: Partial<SuppressionList>,
  ) => {
    if (!suppressionList) {
      return;
    }

    const updatedConnection = await update(activeFbPageId, {
      ...suppressionList,
      ...updatedFields,
    });

    setSuppressionList(updatedConnection);
  };

  const onClickSaveName = () => {
    updateSuppressionList({ name: localName || '' });
    setNameEditMode(false);
  };

  const onClickCancelSaveName = () => {
    setLocalName(suppressionList?.name || '');
    setNameEditMode(false);
  };

  const onClose = async () => {
    viewActions.hideSlideoutMenu();
  };

  const onStatusChange = async () => {
    updateSuppressionList({ enabled: !suppressionList?.enabled });
  };

  const onClickDelete = () => {
    const deleteCallback = async () => {
      if (suppressionList) {
        setTimeout(async () => {
          await deleteList(activeFbPageId, suppressionList);
          viewActions.hideSlideoutMenu();
        }, 300);
      }
    };

    modalActions.openDeleteConfirmModal({
      headerText: 'Are you sure you want to delete this connection?',
      deleteCallback,
      newStyle: true,
    });
  };

  const selectAction = (action: string) => {
    switch (action) {
      case 'delete': {
        return onClickDelete();
      }
      default: {
        return null;
      }
    }
  };

  if (!suppressionList) {
    return null;
  }

  const actionsMenu = [
    {
      disabled: false,
      name: 'Delete',
      value: 'delete',
    },
  ];

  const parsedJsonValue = value => {
    if (!value) {
      return {};
    }

    try {
      return JSON.parse(value);
    } catch (e) {
      return value;
    }
  };

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <div className={styles.headerContent}>
        {nameEditMode ? (
          <Input
            value={localName}
            onChange={e => setLocalName(e.target.value)}
            disabled={!nameEditMode}
            placeholder="Webhook suppression list Name"
            maxLength={50}
            css={styles.descriptionInput}
          />
        ) : (
          <Heading className={styles.headerTitle} size={3}>
            {suppressionList.name}
          </Heading>
        )}

        {!nameEditMode && (
          <EditOutlined
            onClick={() => setNameEditMode(true)}
            className={styles.editIcon}
          />
        )}

        {nameEditMode && (
          <>
            <div onClick={onClickSaveName} className={styles.saveBtn}>
              <CheckOutlined className={styles.checkmark} />
            </div>
            <CloseOutlined
              className={styles.closeIcon}
              onClick={onClickCancelSaveName}
            />
          </>
        )}
        {!nameEditMode && (
          <div className={styles.headerButtons}>
            <Button
              color={
                suppressionList.suppressionWebhook?.url ? undefined : 'blue'
              }
              onClick={onStatusChange}
              css={{
                container: suppressionList.suppressionWebhook?.url
                  ? styles.Button
                  : styles.testConnectionBtn,
              }}
              disabled={!suppressionList.suppressionWebhook?.url}
            >
              {!suppressionList.enabled ? 'Activate' : 'Pause'}
            </Button>
            <ThreeDotMenu
              elements={actionsMenu}
              selectItem={selectAction}
              className={styles.dropdown}
            />
            <CloseOutlined className={styles.closeIcon} onClick={onClose} />
          </div>
        )}
      </div>
      <SlideoutMenu.Content className={styles.content}>
        <div className={styles.container}>
          <div className={styles.configArea}>
            <CollapsibleContentContainer
              ordinal="1"
              title="Suppression List Settings"
              color={colors.accentBlue}
              css={{
                container: styles.collapsibleContainerLast,
              }}
            >
              <EditRequestTypeAndURL
                url={suppressionList.suppressionWebhook?.url || ''}
                requestType={
                  suppressionList.suppressionWebhook?.requestType ||
                  ConnectionRequestType.GET
                }
                onRequestTypeChange={requestType =>
                  updateSuppressionList({
                    suppressionWebhook: { requestType },
                  })
                }
                onUrlChange={url =>
                  updateSuppressionList({ suppressionWebhook: { url } })
                }
              />
            </CollapsibleContentContainer>
            <CollapsibleContentContainer
              ordinal="2"
              title="Request data attributes"
              color={colors.accentBlue}
              css={{ container: styles.collapsibleContainerLast }}
            >
              <div className="edit-connection-panel__attributes-settings">
                <div className={styles.jsonEditors}>
                  <Heading text="Headers" isBold size={5} />

                  <JSONEditor
                    defaultValue={parsedJsonValue(
                      suppressionList.suppressionWebhook?.requestHeaders,
                    )}
                    onBlur={data => {
                      handleJSONFieldsSave({
                        requestHeaders: JSON.stringify(data),
                      });
                    }}
                  />

                  <Heading text="Body" isBold size={5} />

                  <JSONEditor
                    defaultValue={parsedJsonValue(
                      suppressionList.suppressionWebhook?.requestBody,
                    )}
                    onBlur={data =>
                      handleJSONFieldsSave({
                        requestBody: JSON.stringify(data),
                      })
                    }
                  />
                </div>
              </div>
            </CollapsibleContentContainer>
          </div>
        </div>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
